import axios from 'axios'
import { getErrorMessage } from '@/lib/getErrorMessage'

/**
 * Coarse classification of anything the app can throw or reject with.
 * Drives copy, iconography, and whether a retry button is offered.
 */
export type ErrorKind =
  | 'offline'
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'rateLimited'
  | 'server'
  | 'crash'

export interface ClassifiedError {
  kind: ErrorKind
  /** Short, human headline. Names the problem, never the stack. */
  title: string
  /** One or two sentences: what happened and what to do next. */
  description: string
  /** Whether re-running the same request has a plausible chance of working. */
  retryable: boolean
  /** HTTP status when the failure came from the API. */
  status?: number
  /** Server-supplied message, humanized. Shown as supporting detail, not the headline. */
  detail?: string
}

const KIND_COPY: Record<ErrorKind, Pick<ClassifiedError, 'title' | 'description' | 'retryable'>> = {
  offline: {
    title: "You're offline",
    description:
      'Your device lost its internet connection. Reconnect and this will pick up where it left off.',
    retryable: true,
  },
  network: {
    title: "Couldn't reach the server",
    description:
      'The request never got a response. This is usually a brief connection problem — try again in a moment.',
    retryable: true,
  },
  timeout: {
    title: 'The request timed out',
    description: 'The server took too long to respond. It may be under load right now.',
    retryable: true,
  },
  unauthorized: {
    title: 'Your session has expired',
    description: 'Sign in again to continue where you left off.',
    retryable: false,
  },
  forbidden: {
    title: "You don't have access to this",
    description:
      'Your role does not include permission for this data. Ask an administrator if you need it.',
    retryable: false,
  },
  notFound: {
    title: "This doesn't exist",
    description:
      'The item may have been deleted, or the link that brought you here is out of date.',
    retryable: false,
  },
  conflict: {
    title: "That change conflicts with what's saved",
    description: 'Someone may have edited this at the same time. Reload to see the current version.',
    retryable: true,
  },
  validation: {
    title: "Some details weren't accepted",
    description: 'Review the highlighted fields and try again.',
    retryable: false,
  },
  rateLimited: {
    title: 'Too many requests',
    description: 'Slow down for a moment, then try again.',
    retryable: true,
  },
  server: {
    title: 'Something broke on our side',
    description:
      "This isn't your fault. Trying again often works — if it keeps happening, contact your administrator.",
    retryable: true,
  },
  crash: {
    title: 'This section failed to load',
    description:
      'An unexpected error stopped this part of the page from rendering. The rest of the app is still fine.',
    retryable: true,
  },
}

function kindFromStatus(status: number): ErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 409) return 'conflict'
  if (status === 422 || status === 400) return 'validation'
  if (status === 429) return 'rateLimited'
  if (status >= 500) return 'server'
  return 'crash'
}

/**
 * Classifies an unknown thrown value into a kind plus user-facing copy.
 * `fallbackKind` shapes non-HTTP failures — 'crash' for render errors caught by
 * a boundary, 'network' for a rejected data fetch.
 */
export function classifyError(error: unknown, fallbackKind: ErrorKind = 'crash'): ClassifiedError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { ...KIND_COPY.offline, kind: 'offline' }
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    if (typeof status === 'number') {
      const kind = kindFromStatus(status)
      const detail = getErrorMessage(error)
      return {
        ...KIND_COPY[kind],
        kind,
        status,
        // Suppress the detail when it merely restates the headline.
        detail: detail && detail !== KIND_COPY[kind].title ? detail : undefined,
      }
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return { ...KIND_COPY.timeout, kind: 'timeout' }
    }
    return { ...KIND_COPY.network, kind: 'network' }
  }

  const message = getErrorMessage(error)
  return {
    ...KIND_COPY[fallbackKind],
    kind: fallbackKind,
    detail: message && message !== 'Something went wrong' ? message : undefined,
  }
}
